
/*
  FeeManager for Dawn theme
  - OOP refactor of automated fee management
  - Sequential batch updates to /cart endpoints
  - Robust guards against missing Liquid variables
  - Prevents recursion/infinite loops via token and isUpdating flags
  - Preserves compatibility: exposes cartUpdateManager() global
  - No external deps
*/

(() => {
  'use strict';

  // Safely read Liquid-injected constants; if missing, default to null/0/""
  const CONFIG = {
    SET_UP_FEE_PRODUCT: (typeof SET_UP_FEE_PRODUCT !== 'undefined') ? SET_UP_FEE_PRODUCT : null,
    SET_UP_LIMIT: (typeof SET_UP_LIMIT !== 'undefined') ? Number(SET_UP_LIMIT) : 0,
    PRINTED_EMBROIDERY_FEE: (typeof PRINTED_EMBROIDERY_FEE !== 'undefined') ? PRINTED_EMBROIDERY_FEE : null,
    PRINTED_EMBROIDERY_FEE_MESSAGE: (typeof PRINTED_EMBROIDERY_FEE_MESSAGE !== 'undefined') ? String(PRINTED_EMBROIDERY_FEE_MESSAGE) : '',
    ADDITIONAL_ARTWORK_FEE: (typeof ADDITIONAL_ARTWORK_FEE !== 'undefined') ? ADDITIONAL_ARTWORK_FEE : null,
    ADDITIONAL_ARTWORK_FEE_MESSAGE: (typeof ADDITIONAL_ARTWORK_FEE_MESSAGE !== 'undefined') ? String(ADDITIONAL_ARTWORK_FEE_MESSAGE) : '',
    ADDITIONAL_ARTWORK_SETUP_FEE: (typeof ADDITIONAL_ARTWORK_SETUP_FEE !== 'undefined') ? ADDITIONAL_ARTWORK_SETUP_FEE : null
  };

  // Small utility helpers
  const isDefined = v => typeof v !== 'undefined' && v !== null;
  const toNumber = v => Number(v || 0);
  const toString = v => (v === null || v === undefined) ? '' : String(v);

  // Debounce helper
  function debounce(fn, wait = 250) {
    let t;
    return function(...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  // Simple queue runner: sequentially runs promises
  class AsyncQueue {
    constructor() {
      this.queue = [];
      this.running = false;
    }

    push(task) {
      this.queue.push(task);
      this.runNext();
    }

    async runNext() {
      if (this.running) return;
      const next = this.queue.shift();
      if (!next) return;
      this.running = true;
      try {
        await next();
      } catch (e) {
        console.error('AsyncQueue task error', e);
      } finally {
        this.running = false;
        // allow next tick
        setTimeout(() => this.runNext(), 0);
      }
    }
  }

  // FeeManager class encapsulates the fee logic and side effects
  class FeeManager {
    constructor(config) {
      this.config = config;
      this.isUpdating = false; // prevents concurrent runs
      this.lastFingerprint = null; // prevents recursive loops via state fingerprinting
      this.queue = new AsyncQueue();

      // Bind a debounced public method for UI usage
      this.scheduleUpdate = debounce(() => this.updateFromCart(), 300).bind(this);

      // Keep an exported name for backward compat
      window.cartUpdateManager = this.scheduleUpdate.bind(this);

      // Attempt to automatically run once on load (after a tick so other scripts can define globals)
      setTimeout(() => this.scheduleUpdate(), 500);

      // Hook into cart events if CartItems class dispatches a custom event (many themes do this)
      document.addEventListener('cart:updated', () => this.scheduleUpdate());

      // Also listen for DOM-based triggers commonly used
      document.addEventListener('change', (e) => {
        if (e.target && (e.target.matches('[name^="updates"]') || e.target.closest('.product-form'))) {
          this.scheduleUpdate();
        }
      }, { capture: true });
    }

    // Create a lightweight fingerprint of cart-relevant state to detect recursion
    fingerprint(items) {
      // fingerprint key: sorted list of id:key:qty strings
      if (!items || !Array.isArray(items)) return '';
      const parts = items.map(i => `${toString(i.id)}:${toString(i.key)}:${toNumber(i.quantity)}`).sort();
      return parts.join('|');
    }

    // Main read -> decide -> apply flow
    // async updateFromCart() {
    //   if (this.isUpdating) return; // avoid concurrent runs
    //   if (!isDefined(this.config.SET_UP_FEE_PRODUCT)) return; // no config, nothing to do

    //   this.isUpdating = true;

    //   try {
    //     const cart = await this.fetchCart();
    //     const items = cart?.items || [];

    //     // Compute fingerprint and bail if it's identical to last run (prevents recursion)
    //     const fp = this.fingerprint(items);
    //     if (fp && fp === this.lastFingerprint) {
    //       // already in this state — nothing to do
    //       this.isUpdating = false;
    //       return;
    //     }

    //     // Determine desired fee quantities
    //     const { updateOps, addOps } = this.decideFees(items.slice());

    //     if (updateOps.length === 0 && addOps.length === 0) {
    //       this.lastFingerprint = fp; // remember state
    //       this.isUpdating = false;
    //       return;
    //     }

    //     // Execute updates sequentially (changes first, then adds). Use queue to avoid races.
    //     await this.executeBatch(updateOps, addOps);

    //     // Fetch the cart again to store fingerprint and trigger UI updates via theme render
    //     const newCart = await this.fetchCart();
    //     this.lastFingerprint = this.fingerprint(newCart.items || []);

    //     // Tell theme components to re-render sections (Dawn uses sections APIs)
    //     this.refreshThemeSections();
    //     setTimeout(() => {
    //       document.querySelector('button#checkout').disabled = false;
    //     }, 500);

    //   } catch (err) {
    //     console.error('FeeManager updateFromCart error', err);
    //   } finally {
    //     this.isUpdating = false;
    //   }
    // }

    async updateFromCart() {
  if (this.isUpdating) return; // Already updating, skip
  if (!isDefined(this.config.SET_UP_FEE_PRODUCT)) return; // No config, nothing to do

  this.isUpdating = true;

  try {
    const cart = await this.fetchCart();
    const items = cart?.items || [];

    // Compute fingerprint and bail if it's identical to last run (prevents recursion)
    const fp = this.fingerprint(items);
    if (fp && fp === this.lastFingerprint) {
      // already in this state — nothing to do
      return;
    }

    // Determine desired fee quantities
    const { updateOps, addOps } = this.decideFees(items.slice());

    if (updateOps.length === 0 && addOps.length === 0) {
      this.lastFingerprint = fp; // remember state
      return;
    }

    // Execute updates sequentially (changes first, then adds). Use queue to avoid races.
    await this.executeBatch(updateOps, addOps);

    // Fetch the cart again to store fingerprint and trigger UI updates via theme render
    const newCart = await this.fetchCart();
    this.lastFingerprint = this.fingerprint(newCart.items || []);

    // Tell theme components to re-render sections (Dawn uses sections APIs)
    this.refreshThemeSections();

    // Enable button after all fee operations complete
    if (typeof window.CheckoutButtonManager !== 'undefined') {
      window.CheckoutButtonManager.enable(100);
    }
  } catch (err) {
    console.error('FeeManager updateFromCart error', err);
    // Re-enable button even if there's an error to recover state
    if (typeof window.CheckoutButtonManager !== 'undefined') {
      window.CheckoutButtonManager.enable(100);
    }
  } finally {
    this.isUpdating = false;
  }
}
    async fetchCart() {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Failed to fetch cart');
      return res.json();
    }

    decideFees(items) {
      // Items is the raw cart.items array
      let totalValidQuantity = 0; // for setup fee
      let totalQuantity = 0; // for printed/embroidery
      let additionalTotalQuantity = 0; // for additional artwork
      const EXCLUDED_TYPES = ['sample', 'none'];

  // Defensive: ensure props access doesn't throw

      for (const item of items) {
        // Skip existing fee line items by id coercion (don't count fees as products)
        if (toString(item.id) === toString(this.config.SET_UP_FEE_PRODUCT) ||
            toString(item.id) === toString(this.config.PRINTED_EMBROIDERY_FEE) ||
            toString(item.id) === toString(this.config.ADDITIONAL_ARTWORK_FEE) ||
            toString(item.id) === toString(this.config.ADDITIONAL_ARTWORK_SETUP_FEE)) continue;
     
         
        const raw = item.properties && item.properties['Decorations Type'];
        const decorationType = toString(raw).toLowerCase() || 'none';
        const quantity = toNumber(item.quantity);

        if (!EXCLUDED_TYPES.includes(decorationType)) {
          totalValidQuantity += quantity;
        }

        // Must use the SAME exclusion list as the setup fee above. This previously
        // read `decorationType && decorationType !== 'sample'`, which excluded
        // samples but NOT 'none' — and since decorationType defaults to the string
        // 'none' (truthy) for any item with no Decorations Type property, every
        // undecorated item was billed a Printed/Embroidery Fee. That is how a Goat
        // Gear hoodie reached checkout with a fee line on it.
        if (!EXCLUDED_TYPES.includes(decorationType)) {
          totalQuantity += quantity;

          // Detect additional artwork via the MultiVariants property key
          // "Additional Artwork " — the trailing space is intentional, that is how
          // MV submits it due to bracket parsing.
          const hasAdditional = item.properties && (
            item.properties['Additional Artwork '] != null ||
            Number(item.properties['_mv_po_surcharge']) > 0
          );
          if (hasAdditional) {
            additionalTotalQuantity += quantity;
          }
        }
      }      const updateOps = [];
      const addOps = [];

      // find existing fee items by cart item key
      const setupProductLine = items.find(i => toString(i.id) === toString(this.config.SET_UP_FEE_PRODUCT));
      const printedLine = items.find(i => toString(i.id) === toString(this.config.PRINTED_EMBROIDERY_FEE));
      const additionalLine = items.find(i => toString(i.id) === toString(this.config.ADDITIONAL_ARTWORK_FEE));
      const additionalSetupLine = items.find(i => toString(i.id) === toString(this.config.ADDITIONAL_ARTWORK_SETUP_FEE));

      // setup fee logic
      const shouldAddSetup = totalValidQuantity > 0 && totalValidQuantity <= toNumber(this.config.SET_UP_LIMIT);
      const setupCount = shouldAddSetup ? 1 : 0;
  // Apply ADDITIONAL_ARTWORK_SETUP_FEE based solely on the total quantity of
  // products that have the "Additional Upload Option" property. If the
  // total is > 0 and less than or equal to SET_UP_LIMIT, we add the setup
  // fee (quantity = 1). If the total is greater than SET_UP_LIMIT, remove it.
  const shouldAddAdditionalSetup = additionalTotalQuantity > 0 && additionalTotalQuantity <= toNumber(this.config.SET_UP_LIMIT);

      if (setupProductLine) {
        if (setupCount === 0) updateOps.push({ action: 'change', body: { id: setupProductLine.key, quantity: 0 } });
        else if (toNumber(setupProductLine.quantity) !== setupCount) updateOps.push({ action: 'change', body: { id: setupProductLine.key, quantity: setupCount } });
      } else if (setupCount > 0) {
        addOps.push({ action: 'add', body: { id: this.config.SET_UP_FEE_PRODUCT, quantity: setupCount, properties: { Limit: `Under ${toNumber(this.config.SET_UP_LIMIT) + 1} Quantity Setup Fee` } } });
      }

      // printed/embroidery fee
      if (printedLine) {
        if (totalQuantity === 0) updateOps.push({ action: 'change', body: { id: printedLine.key, quantity: 0 } });
        else if (toNumber(printedLine.quantity) !== totalQuantity) updateOps.push({ action: 'change', body: { id: printedLine.key, quantity: totalQuantity } });
      } else if (totalQuantity > 0) {
        addOps.push({ action: 'add', body: { id: this.config.PRINTED_EMBROIDERY_FEE, quantity: totalQuantity, properties: { Message: this.config.PRINTED_EMBROIDERY_FEE_MESSAGE } } });
      }

      // additional artwork fee
      if (additionalLine) {
        if (additionalTotalQuantity === 0) updateOps.push({ action: 'change', body: { id: additionalLine.key, quantity: 0 } });
        else if (toNumber(additionalLine.quantity) !== additionalTotalQuantity) updateOps.push({ action: 'change', body: { id: additionalLine.key, quantity: additionalTotalQuantity } });
      } else if (additionalTotalQuantity > 0) {
        addOps.push({ action: 'add', body: { id: this.config.ADDITIONAL_ARTWORK_FEE, quantity: additionalTotalQuantity, properties: { Message: this.config.ADDITIONAL_ARTWORK_FEE_MESSAGE } } });
      }

      // additional artwork setup fee
      if (additionalSetupLine) {
        if (!shouldAddAdditionalSetup) updateOps.push({ action: 'change', body: { id: additionalSetupLine.key, quantity: 0 } });
        else if (toNumber(additionalSetupLine.quantity) !== 1) updateOps.push({ action: 'change', body: { id: additionalSetupLine.key, quantity: 1 } });
      } else if (shouldAddAdditionalSetup) {
        addOps.push({ action: 'add', body: { id: this.config.ADDITIONAL_ARTWORK_SETUP_FEE, quantity: 1, properties: { Limit: `Additional Artwork Setup Fee (Under ${toNumber(this.config.SET_UP_LIMIT) + 1} Quantity)` } } });
      }

      return { updateOps, addOps };
    }

    // Execute ops sequentially: all change ops first, then add ops
    async executeBatch(updateOps, addOps) {
      // helper: provides the same sections rendering hints used elsewhere in the theme
      const getSectionsToRender = () => {
        return [
          { id: 'main-cart-items', section: document.getElementById('main-cart-items')?.dataset?.id, selector: '.js-contents' },
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
          { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
          { id: 'main-cart-footer', section: document.getElementById('main-cart-footer')?.dataset?.id, selector: '.js-contents' }
        ].filter(s => s.section);
      };

      // helper to update DOM sections from the JSON response (mirrors CartItems.getSectionInnerHTML)
      const updateSectionsFromResponse = (sectionsJson, fullJson) => {
        try {
          const sections = getSectionsToRender();
          sections.forEach((section) => {
            const element = document.getElementById(section.id);
            if (!element) return;
            const container = element.querySelector(section.selector) || element;
            const html = sectionsJson[section.section] || '';
            if (!html) return;
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const inner = parsed.querySelector(section.selector)?.innerHTML ?? parsed.body.innerHTML;
            container.innerHTML = inner;
          });
          // If the server returned an item_count (available on the full JSON), toggle empty state classes to match cart.js behavior
          const itemCount = fullJson && typeof fullJson.item_count !== 'undefined' ? Number(fullJson.item_count) : null;
          if (itemCount !== null) {
            const isEmpty = itemCount === 0;
            // toggle on the cart-items custom element
            const cartItemsEl = document.querySelector('cart-items');
            if (cartItemsEl) cartItemsEl.classList.toggle('is-empty', isEmpty);
            const cartFooter = document.getElementById('main-cart-footer');
            if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
            const cartDrawerWrapper = document.querySelector('cart-drawer');
            if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', isEmpty);
          }
        } catch (e) {
          console.warn('updateSectionsFromResponse failed', e);
        }
      };

      // helper to perform a cart endpoint call; ensures the request asks the server to render sections
      const call = async (action, body) => {
        const url = `/cart/${action}.js`;

        // attach sections rendering hints if not already present
        const sectionsToRender = getSectionsToRender().map(s => s.section);
        const fullBody = Object.assign({}, body);
        if (!fullBody.sections && sectionsToRender.length) {
          fullBody.sections = sectionsToRender;
          fullBody.sections_url = window.location.pathname;
        }

        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fullBody),
          credentials: 'same-origin'
        });
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          console.error('Cart API error', action, res.status, text);
          throw new Error('Cart API error');
        }

        // attempt to parse JSON; some endpoints may return cart JSON with `sections`
        const text = await res.text().catch(() => '');
        if (!text) return null;
        try {
          const json = JSON.parse(text);
          // if server returned rendered sections, update the DOM immediately
          if (json && json.sections) {
            updateSectionsFromResponse(json.sections, json);
          }
          return json;
        } catch (e) {
          // not JSON; return null
          return null;
        }
      };

      // push each change op into queue and wait for queue to drain
      for (const op of updateOps) {
        await new Promise((resolve) => this.queue.push(async () => { await call(op.action, op.body); resolve(); }));
      }

      for (const op of addOps) {
        await new Promise((resolve) => this.queue.push(async () => { await call(op.action, op.body); resolve(); }));
      }
    }

    // Trigger Dawn section rendering where appropriate
    refreshThemeSections() {
      // Many themes use sections via fetch or window.Shopify API. We'll trigger a few common hooks.
      try {
        // If theme provides a cart drawer or cart sections, the cart-change flow in theme.js should re-render.
        // Trigger a global event that cart components can listen to
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

        // Also attempt to call any known global functions used in this theme
        if (window.BOLD && BOLD.common && BOLD.common.eventEmitter && typeof BOLD.common.eventEmitter.emit === 'function') {
          BOLD.common.eventEmitter.emit('BOLD_COMMON_cart_loaded');
        }
      } catch (e) {
        console.warn('refreshThemeSections failed', e);
      }
    }
  }

  // Instantiate the manager
  const feeManager = new FeeManager(CONFIG);

  // Expose for debugging
  window.FeeManagerInstance = feeManager;
})();
